import React, { useEffect, useState } from "react";
import { N8 } from "@/components/BrandMark";
import { endpoints } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { HelpCircle, Sparkles, Check, X, MessageSquareText } from "lucide-react";

const FALLBACK_REACTIONS = [
  { slug: "inspired",    emoji: "🌱", label: "Inspired Me" },
  { slug: "celebrating", emoji: "👏", label: "Celebrating You" },
  { slug: "keep_going",  emoji: "💪", label: "Keep Going" },
  { slug: "learned",     emoji: "✨", label: "Learned Something" },
  { slug: "supporting",  emoji: "❤️", label: "Supporting Your Journey" },
  { slug: "congrats",    emoji: "🎉", label: "Congratulations" },
  { slug: "proud",       emoji: "🙌", label: "Proud of You" },
  { slug: "thanks",      emoji: "⭐", label: "Thank You for Sharing" },
  { slug: "see_you",     emoji: "👁️", label: "I See You" },
  { slug: "feel_you",    emoji: "🫂", label: "I Feel You" },
  { slug: "hear_you",    emoji: "👂", label: "I Hear You" },
];

let _cachedReactions = null;
async function loadReactions() {
  if (_cachedReactions) return _cachedReactions;
  try {
    const { data } = await endpoints.meta();
    _cachedReactions = data.community_reactions?.length ? data.community_reactions : FALLBACK_REACTIONS;
  } catch {
    _cachedReactions = FALLBACK_REACTIONS;
  }
  return _cachedReactions;
}

/**
 * PostReactions — the 8 meaningful community reactions on a post.
 * Replaces the plain "like" tap with meaningful nudges that recognize effort,
 * honesty, and progress rather than popularity.
 */
export function PostReactions({ post, onChanged }) {
  const [kinds, setKinds] = useState(FALLBACK_REACTIONS);
  const [pending, setPending] = useState(false);
  useEffect(() => { loadReactions().then(setKinds); }, []);

  const yours = post.your_reactions || [];
  const summary = post.reactions_summary || {};
  const total = post.reactions_total || 0;

  const react = async (slug) => {
    if (pending) return;
    setPending(true);
    try {
      const { data } = await endpoints.reactPost(post.id, slug);
      onChanged?.(data);
    } catch {
      toast.error("Couldn't send that reaction. Try again.");
    } finally {
      setPending(false);
    }
  };

  // Compact recap: show the top-3 reactions inline (emoji + count) if any.
  const top = Object.entries(summary)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={`post-reactions-${post.id}`}
          className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border hairline text-sm tab-link ${
            yours.length > 0 ? "bg-[hsl(var(--sand))]" : "hover:bg-[hsl(var(--sand))]"
          }`}
          aria-label="React to this journey"
        >
          {top.length === 0 ? (
            <>
              <Sparkles size={13} className="text-[hsl(var(--clay))]" />
              <span>React</span>
            </>
          ) : (
            <>
              {top.map(([slug, count]) => {
                const meta = kinds.find((k) => k.slug === slug);
                if (!meta) return null;
                return (
                  <span key={slug} className="inline-flex items-center gap-0.5">
                    <span aria-hidden>{meta.emoji}</span>
                    <span className="tabular-nums text-[hsl(var(--muted-foreground))]">{count}</span>
                  </span>
                );
              })}
              {total > 0 && <span className="text-[hsl(var(--muted-foreground))] text-xs">· {total}</span>}
            </>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-64 p-2 bg-[hsl(var(--paper))] border hairline"
        data-testid={`post-reactions-panel-${post.id}`}
      >
        <div className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))] px-2 pt-1 pb-2">
          Send meaningful encouragement
        </div>
        <div className="grid grid-cols-1">
          {kinds.map((k) => {
            const on = yours.includes(k.slug);
            const count = summary[k.slug] || 0;
            return (
              <button
                key={k.slug}
                type="button"
                onClick={() => react(k.slug)}
                data-testid={`reaction-${post.id}-${k.slug}`}
                aria-pressed={on}
                disabled={pending}
                className={`w-full text-left flex items-center gap-2.5 px-2 py-1.5 rounded text-sm ${
                  on ? "bg-[hsl(var(--sand))] text-[hsl(var(--fg))]" : "hover:bg-[hsl(var(--sand))]/60"
                }`}
              >
                <span aria-hidden className="text-base w-5 text-center">{k.emoji}</span>
                <span className="flex-1">{k.label}</span>
                {count > 0 && (
                  <span className="text-[11px] text-[hsl(var(--muted-foreground))] tabular-nums">{count}</span>
                )}
                {on && <Check size={12} className="text-[hsl(var(--moss))]" />}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/**
 * AskQuestion — button + dialog that lets a member ask a structured question
 * about a journey. N8 Guide may draft a suggested answer from the creator's
 * prior posts; that draft is held until the creator approves it.
 */
export function AskQuestion({ post, isCreator, onAsked }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [posting, setPosting] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const submit = async () => {
    const content = text.trim();
    if (!content) return;
    setPosting(true);
    try {
      const { data } = await endpoints.askQuestion(post.id, content);
      setLastResult(data);
      setText("");
      if (data.suggested_answer) {
        toast.success("N8 Guide drafted a suggested answer for the creator.");
      } else {
        toast.success("Question sent. The creator will be notified.");
      }
      onAsked?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send that question.");
    } finally {
      setPosting(false);
    }
  };

  if (isCreator) {
    // Creator sees the answer/manage entry point rather than an ask button.
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setLastResult(null); }}>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid={`ask-question-btn-${post.id}`}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border hairline text-sm tab-link hover:bg-[hsl(var(--sand))]"
      >
        <HelpCircle size={13} className="text-[hsl(var(--clay))]" />
        Ask a Question
      </button>
      <DialogContent className="bg-[hsl(var(--paper))] max-w-lg" data-testid={`ask-question-dialog-${post.id}`}>
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight">Ask about this journey</DialogTitle>
          <DialogDescription className="text-[hsl(var(--muted-foreground))]">
            Your question is sent to <strong>{post.author}</strong>. <N8 /> Guide may draft a
            suggested answer from their prior posts, which they can approve or edit.
          </DialogDescription>
        </DialogHeader>
        {!lastResult ? (
          <>
            <Textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={5}
              maxLength={800}
              data-testid="ask-question-textarea"
              placeholder="What would you like to learn from this journey?"
              className="min-h-[120px]"
            />
            <div className="text-[11px] text-[hsl(var(--muted-foreground))] text-right">{text.length}/800</div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setOpen(false)}
                data-testid="ask-question-cancel"
                className="rounded-full"
              >
                Cancel
              </Button>
              <Button
                onClick={submit}
                disabled={posting || !text.trim()}
                data-testid="ask-question-submit"
                className="rounded-full bg-[hsl(var(--clay))] text-[hsl(var(--paper))] hover:bg-[hsl(var(--clay))]/90"
              >
                {posting ? "Sending…" : "Send question"}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <div className="space-y-3" data-testid="ask-question-result">
            {lastResult.suggested_answer ? (
              <div className="p-4 rounded-md border hairline bg-[hsl(var(--sand))]/40">
                <div className="text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--moss))]">
                  <N8 /> Guide drafted a suggested answer
                </div>
                <p className="mt-2 leading-relaxed">{lastResult.suggested_answer}</p>
                <div className="mt-3 text-[11px] text-[hsl(var(--muted-foreground))]">
                  Waiting for {post.author} to approve or edit before it becomes public.
                </div>
              </div>
            ) : (
              <div className="p-4 rounded-md border hairline bg-[hsl(var(--paper))]">
                <p className="leading-relaxed">
                  Question sent. {post.author} will be notified and can answer in their own words.
                </p>
              </div>
            )}
            <DialogFooter>
              <Button
                onClick={() => setOpen(false)}
                data-testid="ask-question-done"
                className="rounded-full bg-[hsl(var(--fg))] text-[hsl(var(--paper))]"
              >
                Done
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * JourneyQnA — renders the knowledge library of a post: approved answers
 * everyone can read, plus (for the creator only) the pending queue with a
 * one-tap approve button on N8-Guide-drafted answers.
 */
export function JourneyQnA({ post }) {
  const [data, setData] = useState({ answered: [], pending: [], is_creator: false });
  const [expanded, setExpanded] = useState(false);
  const [draftEdits, setDraftEdits] = useState({});
  const [pendingId, setPendingId] = useState(null);

  const load = async () => {
    try {
      const { data } = await endpoints.listQuestions(post.id);
      setData(data);
    } catch { /* silent */ }
  };
  useEffect(() => { load(); }, [post.id]);

  const answeredCount = (data.answered || []).length;
  const pendingCount = (data.pending || []).length;
  const showToggle = answeredCount > 0 || (data.is_creator && pendingCount > 0);
  if (!showToggle) return null;

  const approve = async (q) => {
    setPendingId(q.id);
    try {
      await endpoints.approveAnswer(q.id, draftEdits[q.id]);
      toast.success("Answer approved — now part of your journey library.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't approve.");
    } finally { setPendingId(null); }
  };

  const answer = async (q) => {
    const content = (draftEdits[q.id] || "").trim();
    if (!content) return;
    setPendingId(q.id);
    try {
      await endpoints.answerQuestion(q.id, content);
      toast.success("Answered — thank you for sharing.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't answer.");
    } finally { setPendingId(null); }
  };

  return (
    <div className="mt-4 border-t hairline pt-3" data-testid={`journey-qna-${post.id}`}>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        data-testid={`journey-qna-toggle-${post.id}`}
        className="w-full flex items-center justify-between text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))] tab-link"
      >
        <span className="inline-flex items-center gap-2">
          <MessageSquareText size={12} className="text-[hsl(var(--moss))]" />
          Journey knowledge · {answeredCount} answered
          {data.is_creator && pendingCount > 0 ? ` · ${pendingCount} awaiting you` : ""}
        </span>
        <span>{expanded ? "hide" : "view"}</span>
      </button>
      {expanded && (
        <div className="mt-3 space-y-4">
          {(data.answered || []).map((q) => (
            <div
              key={q.id}
              className="p-3 rounded-md bg-[hsl(var(--paper))] border hairline"
              data-testid={`qna-answered-${q.id}`}
            >
              <div className="text-[11px] uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">
                {q.asker_name} asked
              </div>
              <div className="mt-1 text-sm leading-relaxed">{q.content}</div>
              <div className="mt-3 pl-3 border-l-2 border-[hsl(var(--moss))]">
                <div className="text-[11px] uppercase tracking-[0.2em] text-[hsl(var(--moss))]">
                  {q.answered_by === "creator" ? `${post.author} answered` : <>{post.author} approved <N8 /> Guide's draft</>}
                </div>
                <div className="mt-1 text-sm leading-relaxed">{q.answer}</div>
              </div>
            </div>
          ))}
          {data.is_creator && (data.pending || []).map((q) => {
            const draft = draftEdits[q.id] ?? (q.suggested_answer || "");
            return (
              <div
                key={q.id}
                className="p-3 rounded-md bg-[hsl(var(--sand))]/40 border hairline"
                data-testid={`qna-pending-${q.id}`}
              >
                <div className="text-[11px] uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">
                  {q.asker_name} asked
                </div>
                <div className="mt-1 text-sm leading-relaxed">{q.content}</div>
                {q.suggested_answer && (
                  <div className="mt-2 text-[11px] uppercase tracking-[0.2em] text-[hsl(var(--moss))]">
                    <N8 /> Guide drafted a suggested answer — edit if you like, then approve.
                  </div>
                )}
                <Textarea
                  value={draft}
                  onChange={(e) => setDraftEdits((prev) => ({ ...prev, [q.id]: e.target.value }))}
                  rows={4}
                  placeholder="Your answer in your own words…"
                  data-testid={`qna-answer-input-${q.id}`}
                  className="mt-2 min-h-[100px] text-sm"
                />
                <div className="mt-2 flex gap-2 flex-wrap">
                  {q.suggested_answer && (
                    <Button
                      onClick={() => approve(q)}
                      disabled={pendingId === q.id}
                      data-testid={`qna-approve-${q.id}`}
                      size="sm"
                      className="rounded-full bg-[hsl(var(--moss))] text-[hsl(var(--paper))] hover:bg-[hsl(var(--moss))]/90"
                    >
                      <Check size={13} className="mr-1.5" /> Approve draft
                    </Button>
                  )}
                  <Button
                    onClick={() => answer(q)}
                    disabled={pendingId === q.id || !(draftEdits[q.id] || "").trim()}
                    data-testid={`qna-answer-${q.id}`}
                    size="sm"
                    variant="outline"
                    className="rounded-full"
                  >
                    Send my answer
                  </Button>
                </div>
              </div>
            );
          })}
          {answeredCount === 0 && pendingCount === 0 && (
            <div className="text-sm text-[hsl(var(--muted-foreground))]">
              No questions yet. When one arrives, the answer stays here as a journey resource.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
